import React, { useState, useEffect, useRef } from 'react';
import { useExecutePrefetch, useMinetestConsole, usePrefetchData, useStorageManager } from '../../utils/GlobalContext';
import { type GameId, type GameOptions } from '../../App';
import { PROXIES, SUPPORTED_LANGUAGES } from '../../utils/common';

interface StartScreenProps {
  onStartGame: (options: GameOptions) => void;
  updateGameOptions: (options: Partial<GameOptions>) => void;
  currentOptions: GameOptions;
  zipLoaderPromise: Promise<Uint8Array> | null;
  setZipLoaderPromise: (promise: Promise<Uint8Array> | null) => void;
  hasFailed?: boolean;
}

const StartScreen: React.FC<StartScreenProps> = ({ onStartGame, updateGameOptions, currentOptions, hasFailed }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(currentOptions.language);
  const [selectedProxy, _setSelectedProxy] = useState<number>(parseInt(localStorage.getItem('luanti_wasm_selected_proxy') || '0'));
  const [selectedStorage] = useState<string>(currentOptions.storagePolicy);
  const [isPreloading, setIsPreloading] = useState(true);
  const [selectedGameId] = useState<GameId>('mineclone2');
  const [gameMode] = useState<'local' | 'host' | 'join'>('local');
  const prefetchData = usePrefetchData();
  const executePrefetch = useExecutePrefetch();
  const minetestConsole = useMinetestConsole();
  const storageManager = useStorageManager();
  const autoStarted = useRef(false);

  const setSelectedProxy = (index: number) => {
    _setSelectedProxy(index);
    localStorage.setItem('luanti_wasm_selected_proxy', index.toString());
  };

  useEffect(() => {
    const index = PROXIES.findIndex(proxy => proxy[0] === currentOptions.proxy);
    if (index !== -1) {
      setSelectedProxy(index);
    }
  }, [currentOptions.proxy]);

  useEffect(() => {
    executePrefetch(selectedGameId);
    if (selectedGameId === 'mineclone2') {
      if (prefetchData.status.voxelibre !== 'done' || prefetchData.status.base !== 'done') {
        setIsPreloading(true);
      }
    } else if (prefetchData.status[selectedGameId] !== 'done' || prefetchData.status.base !== 'done') {
      setIsPreloading(true);
    }
  }, [selectedGameId]);

  useEffect(() => {
    if (selectedGameId === 'mineclone2') {
      if (prefetchData.status.voxelibre === 'done' && prefetchData.status.base === 'done') {
        setIsPreloading(false);
      }
    } else if (prefetchData.status[selectedGameId] === 'done' && prefetchData.status.base === 'done') {
      setIsPreloading(false);
    }
  }, [prefetchData.status, selectedGameId]);

  useEffect(() => {
    if (storageManager) {
      storageManager.initialize({ policy: selectedStorage }, minetestConsole);
    }
  }, [storageManager, selectedStorage]);

  useEffect(() => {
    if (storageManager && minetestConsole) {
      storageManager.setMinetestConsole(minetestConsole);
    }
  }, [storageManager, minetestConsole]);

  useEffect(() => {
    const getDefaultLanguage = () => {
      const supportedLanguageMap = new Map(SUPPORTED_LANGUAGES);
      const fuzzy: string[] = [];
      for (let candidate of navigator.languages) {
        candidate = candidate.replaceAll('-', '_');
        if (supportedLanguageMap.has(candidate)) {
          return candidate;
        }
        const parts = candidate.split('_');
        if (parts.length > 2) {
          const rcandidate = parts.slice(0, 2).join('_');
          if (supportedLanguageMap.has(rcandidate)) {
            return rcandidate;
          }
        }
        if (parts.length > 1 && supportedLanguageMap.has(parts[0])) {
          return parts[0];
        }
        for (let entry of SUPPORTED_LANGUAGES) {
          if (entry[0].split('_')[0] === parts[0]) {
            fuzzy.push(entry[0]);
          }
        }
      }
      if (fuzzy.length > 0) {
        return fuzzy[0];
      }
      return 'en';
    };

    if (currentOptions.language === 'en') {
      const detectedLanguage = getDefaultLanguage();
      setSelectedLanguage(detectedLanguage);
      updateGameOptions({ language: detectedLanguage });
    }
  }, []);

  const handleStartGame = async () => {
    setIsLoading(true);
    try {
      const proxyUrl = PROXIES[selectedProxy][0];
      const gameOptions: GameOptions = {
        language: selectedLanguage,
        proxy: proxyUrl,
        storagePolicy: selectedStorage,
        minetestArgs: currentOptions.minetestArgs,
        // El modo lo decide el hub via App (?pcmode=...). gameId queda en el
        // precargado (mineclone2). joinCode/playerName vienen del query en join.
        // OJO: worldName/realmId/realmCfg también viajan por el query — sin
        // copiarlos acá el host de realm booteaba al menú en vez del mundo.
        mode: currentOptions.mode || gameMode,
        gameId: selectedGameId,
        joinCode: currentOptions.joinCode,
        playerName: currentOptions.playerName,
        worldName: currentOptions.worldName,
        realmId: currentOptions.realmId,
        realmCfg: currentOptions.realmCfg,
      };
      onStartGame(gameOptions);
    } catch (error) {
      minetestConsole.printErr(`Error: ${error}`);
      setIsLoading(false);
    }
  };

  // Auto-start the game as soon as resources are preloaded (no menu screen)
  useEffect(() => {
    if (!isPreloading && !isLoading && !autoStarted.current && !hasFailed) {
      autoStarted.current = true;
      handleStartGame();
    }
  }, [isPreloading, isLoading, hasFailed]);

  const clouds = [
    { top: '8%', s: 50, op: 0.92, dur: 70, delay: 0 },
    { top: '18%', s: 34, op: 0.8, dur: 95, delay: -40 },
    { top: '12%', s: 64, op: 0.96, dur: 55, delay: -18 },
    { top: '27%', s: 42, op: 0.78, dur: 120, delay: -70 },
    { top: '4%', s: 30, op: 0.7, dur: 140, delay: -55 },
    { top: '22%', s: 56, op: 0.88, dur: 82, delay: -25 },
  ];

  return (
    <div
      style={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'linear-gradient(#74b4ef 0%, #9fcdf3 55%, #c7e4fb 100%)',
        fontFamily: 'monospace',
      }}
    >
      <style>{`@keyframes pcDrift { from { transform: translateX(-35vw); } to { transform: translateX(135vw); } }`}</style>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden' }}>
        {clouds.map((c, idx) => (
          <div key={idx} style={{
            position: 'absolute',
            top: c.top,
            left: 0,
            width: c.s,
            height: c.s * 0.7,
            background: '#ffffff',
            opacity: c.op,
            boxShadow: `${c.s * 0.85}px ${c.s * 0.18}px 0 0 #fff, ${c.s * 1.7}px ${c.s * 0.32}px 0 0 #fff, ${c.s * 0.5}px ${-c.s * 0.42}px 0 0 #fff, ${c.s * 2.45}px ${c.s * 0.12}px 0 0 #fff`,
            animation: `pcDrift ${c.dur}s linear infinite`,
            animationDelay: `${c.delay}s`,
          }} />
        ))}
      </div>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'rgba(0,0,0,0.22)' }} />

      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          fontWeight: 700,
          fontSize: 'clamp(40px, 7vw, 86px)',
          color: '#cdd1cb',
          letterSpacing: 4,
          textShadow: '4px 4px 0 #5a5e58, 8px 8px 0 #2b2d29, 10px 10px 0 #141512',
        }}>
          PiolaCraft
        </div>
        <div style={{ color: '#e8e8d8', marginTop: 14, fontSize: 18, textShadow: '1px 1px 0 #000' }}>
          construido sobre el motor luanti
        </div>
        <div style={{ marginTop: 28, fontSize: 18, color: '#fff', textShadow: '1px 1px 0 #000' }}>
          {hasFailed ? (
            <span style={{ color: '#ff6b6b' }}>Error al iniciar el juego. Revisa la consola.</span>
          ) : isLoading ? (
            'iniciando...'
          ) : (
            'cargando recursos...'
          )}
        </div>
        {hasFailed && (
          <button 
            onClick={handleStartGame}
            style={{
              marginTop: 20,
              padding: '10px 24px',
              fontSize: 16,
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              textShadow: '1px 1px 0 #000',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              fontFamily: 'monospace'
            }}
          >
            Reintentar
          </button>
        )}
      </div>

      <div style={{ position: 'absolute', left: 12, bottom: 8, zIndex: 2, color: '#fff', fontSize: 16, textShadow: '1px 1px 0 #000' }}>
        PiolaCraft 0.1 · nativo web
      </div>
      <div style={{ position: 'absolute', right: 12, bottom: 8, zIndex: 2, color: '#fff', fontSize: 16, textShadow: '1px 1px 0 #000' }}>
        libre y modificable
      </div>
    </div>
  );
};

export default StartScreen;
